<template>
  <div class="project-header">
    <div class="header-background">
      <div class="floating-elements">
        <div class="floating-element" v-for="n in 6" :key="n"></div>
      </div>
    </div>
    
    <div class="header-content">
      <div class="section-intro">
        <h1 class="section-title">
          {{ title }}
        </h1>
        <p v-if="subtitle" class="section-subtitle">
          {{ subtitle }}
        </p>
        <div class="title-underline"></div>
        <div class="hero-decoration">
          <div class="decoration-line"></div>
          <div class="decoration-circle"></div>
          <div class="decoration-line"></div>
        </div>
      </div>
    </div>
    
    <div class="scroll-indicator">
      <div class="scroll-arrow">↓</div>
      <span>滚动查看项目</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectHeader',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    }
  }
}
</script>

<style scoped>
.project-header {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-size: 200% 200%;
  animation: gradientShift 8s ease-in-out infinite;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.1;
  z-index: 1;
}

.floating-elements {
  position: relative;
  width: 100%;
  height: 100%;
}

.floating-element {
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.floating-element:nth-child(1) {
  width: 80px;
  height: 80px;
  top: 20%;
  left: 10%;
  animation-delay: 0s;
}

.floating-element:nth-child(2) {
  width: 60px;
  height: 60px;
  top: 60%;
  left: 80%;
  animation-delay: 1s;
}

.floating-element:nth-child(3) {
  width: 100px;
  height: 100px;
  top: 10%;
  left: 70%;
  animation-delay: 2s;
}

.floating-element:nth-child(4) {
  width: 40px;
  height: 40px;
  top: 70%;
  left: 20%;
  animation-delay: 3s;
}

.floating-element:nth-child(5) {
  width: 70px;
  height: 70px;
  top: 30%;
  left: 50%;
  animation-delay: 4s;
}

.floating-element:nth-child(6) {
  width: 50px;
  height: 50px;
  top: 80%;
  left: 60%;
  animation-delay: 5s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}

.header-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 900px;
  padding: 2rem;
}

.section-intro {
  max-width: 1000px;
  margin: 0 auto;
}

.section-title {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 700;
  color: white;
  margin: 0 0 1rem 0;
  line-height: 1.3;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  letter-spacing: 2px;
  background: linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: titleSlideIn 1s ease-out;
}

.section-subtitle {
  font-size: clamp(1rem, 2.5vw, 1.3rem);
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 2rem 0;
  line-height: 1.6;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
  animation: subtitleSlideIn 1s ease-out 0.3s both;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

@keyframes subtitleSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes titleSlideIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.title-underline {
  width: 100px;
  height: 4px;
  background: linear-gradient(90deg, transparent, white, transparent);
  margin: 1.5rem auto;
  border-radius: 2px;
  animation: underlineExpand 1s ease-out 0.5s both;
}

@keyframes underlineExpand {
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 100px;
    opacity: 1;
  }
}

.hero-decoration {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.decoration-line {
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  border-radius: 1px;
}

.decoration-circle {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.scroll-indicator {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  animation: bounce 2s infinite;
  z-index: 2;
}

.scroll-arrow {
  font-size: 1.2rem;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { 
    transform: translateX(-50%) translateY(0); 
  }
  40% { 
    transform: translateX(-50%) translateY(-8px); 
  }
  60% { 
    transform: translateX(-50%) translateY(-4px); 
  }
}

@media (max-width: 768px) {
  .header-content {
    padding: 1rem;
  }
  
  .section-title {
    margin-bottom: 1.5rem;
  }
  
  .floating-element {
    display: none;
  }
  
  .decoration-line {
    width: 40px;
  }
}

@media (max-width: 480px) {
  .hero-decoration {
    gap: 0.5rem;
  }
  
  .decoration-line {
    width: 30px;
  }
  
  .decoration-circle {
    width: 8px;
    height: 8px;
  }
}
</style>